
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Upload, ExternalLink, RefreshCw, CloudUpload, History, RotateCcw, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useLogoSettings } from '@/hooks/use-logo-settings';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const LogoManagement = () => {
  const { toast } = useToast();
  const { currentLogoUrl, logoHistory, isLoading, updateLogo, isUpdating } = useLogoSettings();
  const [localLogoUrl, setLocalLogoUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update local state when current logo loads
  useEffect(() => {
    if (currentLogoUrl && !localLogoUrl) {
      setLocalLogoUrl(currentLogoUrl);
    }
  }, [currentLogoUrl, localLogoUrl]);

  const forceCacheRefresh = async (logoUrl: string) => {
    const cacheBustUrl = `${logoUrl}?v=${Date.now()}`;
    
    // Update all favicon references with cache busting
    const links = document.querySelectorAll('link[rel*="icon"]');
    links.forEach(link => {
      if (link instanceof HTMLLinkElement) {
        link.href = cacheBustUrl;
      }
    });

    // Update apple touch icons
    const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    appleIcons.forEach(icon => {
      if (icon instanceof HTMLLinkElement) {
        icon.href = cacheBustUrl;
      }
    });

    // Update meta tags
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', cacheBustUrl);
    }

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', cacheBustUrl);
    }

    // Update manifest link with cache busting
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink instanceof HTMLLinkElement) {
      manifestLink.href = `/api/manifest.json?v=${Date.now()}`;
    }

    // Force service worker update for PWA
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => {
          registration.update();
          return registration.unregister();
        }));
        
        // Re-register service worker
        if (window.location.protocol === 'https:') {
          await navigator.serviceWorker.register('/sw.js');
        }
      } catch (error) {
        console.warn('Service worker update failed:', error);
      }
    }

    // Store for global access
    window.__currentLogoUrl = logoUrl;
  };

  const handleUpdateLogo = async () => {
    if (!localLogoUrl.trim()) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid logo URL.",
        variant: "destructive"
      });
      return;
    }

    // Update in database
    updateLogo(localLogoUrl);

    // Force cache refresh immediately
    await forceCacheRefresh(localLogoUrl);

    toast({
      title: "Logo Updated",
      description: "Logo has been updated. You may need to refresh the page or clear browser cache to see changes on all devices.",
    });
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload an image file (PNG, JPG, SVG).",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (1MB limit)
    if (file.size > 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please upload an image smaller than 1MB.",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('logoexchangeplatformurl')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('logoexchangeplatformurl')
        .getPublicUrl(fileName);

      const newLogoUrl = publicUrlData.publicUrl;
      setLocalLogoUrl(newLogoUrl);

      toast({
        title: "Upload Successful",
        description: "Logo uploaded successfully! Click 'Save & Apply Logo' to make it active.",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRestoreLogo = async (logoUrl: string) => {
    setLocalLogoUrl(logoUrl);
    // Immediately update the database and refresh
    updateLogo(logoUrl);
    await forceCacheRefresh(logoUrl);
    
    toast({
      title: "Logo Restored",
      description: "Logo has been restored and applied.",
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Logo Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-muted-foreground">Loading logo settings...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Logo Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              After updating the logo, you may need to clear browser cache or refresh the page. On mobile devices, you might need to reinstall the PWA to see the new icon.
            </AlertDescription>
          </Alert>

          {/* Upload Section */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Upload New Logo</Label>
            
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <CloudUpload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">
                {isUploading ? 'Uploading...' : 'Drop your logo here or click to browse'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports PNG, JPG, SVG (max 1MB)
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* URL Input Section */}
          <div className="space-y-2">
            <Label htmlFor="logo-url">Logo URL</Label>
            <Input
              id="logo-url"
              value={localLogoUrl}
              onChange={(e) => setLocalLogoUrl(e.target.value)}
              placeholder="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/..."
            />
            <p className="text-sm text-muted-foreground">
              Upload your logo to Supabase Storage or paste the public URL here.
            </p>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={handleUpdateLogo} 
              disabled={isUpdating || !localLogoUrl || isUploading}
              className="flex-1"
            >
              {isUpdating ? 'Saving...' : 'Save & Apply Logo'}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleRefreshPage}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          <div className="border rounded-lg p-4 bg-muted/50">
            <h4 className="font-medium mb-2">Current Logo Preview</h4>
            <div className="flex items-center gap-4">
              <img 
                src={`${currentLogoUrl || localLogoUrl}?v=${Date.now()}`}
                alt="Current Logo" 
                className="h-16 w-16 object-contain border rounded"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
              <div className="text-sm text-muted-foreground">
                <p>This logo will be used for:</p>
                <ul className="list-disc list-inside mt-1">
                  <li>Browser favicon</li>
                  <li>PWA app icon</li>
                  <li>Apple touch icon</li>
                  <li>Social media previews</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ExternalLink className="h-4 w-4" />
            <a 
              href="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              View Supabase Storage Bucket
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Logo History */}
      {logoHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Logo History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-3">
                {logoHistory.map((entry) => (
                  <div key={entry.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <img 
                      src={`${entry.logo_url}?v=${Date.now()}`}
                      alt="Logo" 
                      className="h-12 w-12 object-contain border rounded"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder.svg';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{entry.logo_url}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.is_active && (
                        <Badge variant="secondary" className="text-xs">Active</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestoreLogo(entry.logo_url)}
                        className="flex items-center gap-1"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
